import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { FIRM } from "@/lib/site";
import { scrollToSection } from "@/hooks/useLenis";

const LINKS = [
  { label: "Practice", href: "#practice" },
  { label: "About", href: "#about" },
  { label: "Services", href: "#services" },
  { label: "Contact", href: "#contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (href) => {
    setOpen(false);
    scrollToSection(href);
  };

  return (
    <motion.header
      data-testid="site-navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1, ease: [0.33, 1, 0.68, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 w-full z-40 transition-colors duration-500 ${
        scrolled ? "bg-[#0A1128]/95 backdrop-blur-md border-b border-[#D4AF37]/20" : "bg-transparent"
      }`}
    >
      <nav className="max-w-[1400px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <a href="#top" data-testid="nav-logo" onClick={(e) => { e.preventDefault(); scrollTo("#top"); }}
           className="flex items-center gap-3">
          <span className="font-serif-display text-xl md:text-2xl text-white leading-none">
            Arjun Singh <span className="text-[#D4AF37]">&amp; Associates</span>
          </span>
        </a>

        <div className="hidden md:flex items-center gap-10">
          {LINKS.map((l) => (
            <button key={l.href} data-testid={`nav-link-${l.label.toLowerCase()}`}
              onClick={() => scrollTo(l.href)}
              className="link-underline font-body text-sm text-white/80 hover:text-white transition-colors">
              {l.label}
            </button>
          ))}
          <button data-testid="nav-book-btn" onClick={() => scrollTo("#contact")}
            className="group inline-flex items-center gap-2 bg-[#D4AF37] text-[#0A1128] px-5 py-2.5 text-sm font-medium hover:bg-white transition-colors">
            Book Consultation
          </button>
        </div>

        <button data-testid="nav-mobile-toggle" onClick={() => setOpen(!open)}
          className="md:hidden text-white p-2">
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0 }} animate={{ height: "auto" }} exit={{ height: 0 }}
            className="md:hidden overflow-hidden bg-[#0A1128] border-t border-[#D4AF37]/20">
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <button key={l.href} data-testid={`nav-mobile-${l.label.toLowerCase()}`}
                  onClick={() => scrollTo(l.href)}
                  className="text-left font-body text-base text-white/85">{l.label}</button>
              ))}
              <button data-testid="nav-mobile-book" onClick={() => scrollTo("#contact")}
                className="bg-[#D4AF37] text-[#0A1128] px-5 py-3 text-sm font-medium">Book Consultation</button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
